import * as React from 'react';
import { T, Var } from 'gt-react';
import { CheckIcon, CaretUpDownIcon } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const DEFAULT_MAX_DISPLAY_ITEMS = 2;

export type AutocompletePropsBase<
  T,
  TMultiSelect extends boolean | undefined,
> = {
  options: T[];
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  getValue: (option: T) => string;
  getLabel: (option: T) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  maxDisplayItems?: number;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  filterFunction?: (option: T, search: string) => boolean;
  allowClear?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverModal?: boolean;
  multiSelect: TMultiSelect;
  onValueChange: TMultiSelect extends true
    ? (values: string[]) => void
    : (value: string) => void;
  value: (TMultiSelect extends true ? string[] : string) | undefined;
  renderTrigger?: TMultiSelect extends true
    ? (values: T[]) => React.ReactNode
    : (value: T) => React.ReactNode;
};

type AutocompleteProps<
  T,
  TProps extends AutocompletePropsBase<T, boolean | undefined> =
    AutocompletePropsBase<T, boolean | undefined>,
> =
  TProps extends AutocompletePropsBase<T, infer U>
    ? U extends boolean | undefined
      ? AutocompletePropsBase<T, U>
      : never
    : never;

type AutocompleteItemProps<T, TMultiSelect extends boolean> = Pick<
  AutocompletePropsBase<T, TMultiSelect>,
  'getValue' | 'getLabel' | 'renderOption'
> & {
  option: T;
  isSelected: boolean;
  onSelect: () => void;
};

// Memoized item component for performance
function AutocompleteItemBase<T, TMultiSelect extends boolean>({
  getValue,
  getLabel,
  option,
  isSelected,
  onSelect,
  renderOption,
}: AutocompleteItemProps<T, TMultiSelect>) {
  const handleSelect = React.useCallback(() => {
    onSelect();
  }, [onSelect]);

  // Fallback to renderOption for backward compatibility
  const content = renderOption ? (
    renderOption(option, isSelected)
  ) : (
    <>
      <CheckIcon
        className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
      />
      <span>{getLabel(option)}</span>
    </>
  );

  return (
    <CommandItem
      key={getValue(option)}
      value={getValue(option)}
      onSelect={handleSelect}
    >
      {content}
    </CommandItem>
  );
}

const AutocompleteItem = React.memo(
  AutocompleteItemBase,
) as typeof AutocompleteItemBase;

function isValueArray(value: string | string[] | undefined): value is string[] {
  return value !== undefined && typeof value !== 'string';
}

function Trigger<T>({
  selectedOptions,
  placeholder,
  multiSelect,
  maxDisplayItems = DEFAULT_MAX_DISPLAY_ITEMS,
  getLabel,
  getValue,
  renderTrigger,
}: Pick<
  AutocompleteProps<T>,
  | 'placeholder'
  | 'multiSelect'
  | 'maxDisplayItems'
  | 'getLabel'
  | 'getValue'
  | 'renderTrigger'
> & {
  selectedOptions: T[];
}) {
  if (selectedOptions.length === 0) {
    return (
      <span className="text-muted-foreground truncate">{placeholder}</span>
    );
  }

  // If renderTrigger is provided, use it (for both single and multi select)
  if (renderTrigger) {
    const customContent = multiSelect
      ? (renderTrigger as (values: T[]) => React.ReactNode)(selectedOptions)
      : (renderTrigger as (value: T) => React.ReactNode)(selectedOptions[0]);

    return <span className="truncate">{customContent}</span>;
  }

  // Default rendering for multiSelect
  if (multiSelect) {
    return (
      <div className="flex gap-1 overflow-hidden">
        {selectedOptions
          .slice(0, Math.min(selectedOptions.length, 3))
          .map((option) => (
            <span
              key={getValue(option)}
              className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-xs whitespace-nowrap flex-shrink-0"
            >
              {getLabel(option)}
            </span>
          ))}
        {selectedOptions.length > 3 && (
          <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-xs whitespace-nowrap flex-shrink-0">
            <T context="count of additional selected items not shown">
              +<Var>{selectedOptions.length - 3}</Var> more
            </T>
          </span>
        )}
      </div>
    );
  }

  // Default rendering for single select
  return <span className="truncate">{getLabel(selectedOptions[0])}</span>;
}

export function Autocomplete<T>({
  options,
  value,
  searchValue,
  onSearchValueChange,
  getValue,
  getLabel,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search options...',
  emptyMessage = 'No results.',
  className,
  disabled = false,
  multiSelect,
  maxDisplayItems = DEFAULT_MAX_DISPLAY_ITEMS,
  renderOption,
  renderTrigger,
  filterFunction,
  allowClear = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  popoverModal,
}: AutocompleteProps<T>) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [_highlightedValue, setHighlightedValue] = React.useState('');

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  // When the search text changes, drop any stale manual highlight so the
  // highlight falls back to the first filtered option (see `highlightedValue`
  // below). Without this, a highlight value from a previous query no longer
  // matches any visible item and cmdk shows nothing selected until the user
  // arrows down — so Enter/Down on a fresh search wouldn't pick the top hit.
  const [prevSearchValue, setPrevSearchValue] = React.useState(searchValue);
  if (searchValue !== prevSearchValue) {
    setPrevSearchValue(searchValue);
    setHighlightedValue('');
  }

  // Normalize value to always be an array for easier handling
  const selectedValues: string[] = React.useMemo(() => {
    if (isValueArray(value)) {
      return value;
    }
    return value ? [value] : [];
  }, [value]);

  const selectedOptions = React.useMemo(() => {
    return options.filter((option) =>
      selectedValues.includes(getValue(option)),
    );
  }, [options, selectedValues, getValue]);

  // Default filter function
  const defaultFilterFunction = React.useCallback(
    (option: T, search: string) => {
      return (
        getLabel(option).toLowerCase().includes(search.toLowerCase()) ||
        getValue(option).toLowerCase().includes(search.toLowerCase())
      );
    },
    [getLabel, getValue],
  );

  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return options;
    const filterFn = filterFunction || defaultFilterFunction;
    return options.filter((option) => filterFn(option, searchValue));
  }, [options, searchValue, filterFunction, defaultFilterFunction]);

  // Keep the first filtered option highlighted so Enter selects it
  const highlightedValue: string = React.useMemo(() => {
    if (_highlightedValue) return _highlightedValue;
    return filteredOptions.length ? getValue(filteredOptions[0]) : '';
  }, [filteredOptions, getValue, _highlightedValue]);

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      if (multiSelect) {
        const newValues: string[] = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        onValueChange(newValues);
      } else {
        const newValue = selectedValues.includes(optionValue)
          ? ''
          : optionValue;
        (onValueChange as (value: string) => void)(newValue);
        setOpen(false);
      }
    },
    [selectedValues, multiSelect, onValueChange, setOpen],
  );

  const handleClear = React.useCallback(() => {
    if (multiSelect) {
      onValueChange([]);
    } else {
      (onValueChange as (value: string) => void)('');
    }
  }, [onValueChange, multiSelect]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between bg-transparent h-8 min-w-0',
            className,
          )}
        >
          <Trigger
            selectedOptions={selectedOptions}
            placeholder={placeholder}
            multiSelect={multiSelect}
            maxDisplayItems={maxDisplayItems}
            getLabel={getLabel}
            getValue={getValue}
            renderTrigger={renderTrigger}
          />
          <CaretUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          shouldFilter={false}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={onSearchValueChange}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(getValue(option));
                return (
                  <AutocompleteItem
                    key={`${getLabel(option)}::${getValue(option)}`}
                    option={option}
                    getLabel={getLabel}
                    getValue={getValue}
                    isSelected={isSelected}
                    onSelect={() => handleSelect(getValue(option))}
                    renderOption={renderOption}
                  />
                );
              })}
            </CommandGroup>
            {allowClear && (
              <CommandGroup className="sticky bottom-0 bg-background">
                <CommandSeparator />
                <CommandItem
                  onSelect={() => {
                    handleClear();
                    setOpen(false);
                  }}
                  className="text-sm justify-center"
                >
                  <span>
                    <T context="button label to clear autocomplete selection">
                      Clear
                    </T>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
